import React, {
  memo,
  useState,
  useRef,
  useMemo,
  useCallback,
  useEffect,
} from 'react';
import { pathOr } from 'ramda';
import WarningIcon from '@mui/icons-material/Warning';
import { CircularProgress, Snackbar, ThemeProvider } from '@mui/material';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import {
  createLogEntryPayload,
  withTrackProps,
} from '@dpdgroupuk/react-event-tracker';

import { fetchVehicles, fetchDepotDrivers } from '../../services/driver';
import {
  getDriverByCode,
  getDriverData,
  getFormattedDriversList,
  getInitialDriverDetailsValues,
  getSortedOdfDriversList,
  getRouteOdfDriver,
  mapDriverForFormModel,
} from '../driverDetails/model';
import ConfirmSnackbar from '../../components/modals/ConfirmSnackbar';
import ModalControlled from '../../components/ModalControlled';
import {
  ARE_YOU_WANT_TO_DISCARD,
  PLEASE_FIX_ERRORS,
  YOU_HAVE_UNSAVED_CHANGES,
  SOMETHING_WENT_WRONG,
} from '../driverDetails/messages';
import theme from '../../components/styles/driverDetails';
import { ValidationEffect } from '../driverDetails/components/validationEffect';
import { wrapPromise } from '../../utils/promise';
import useLocationQueryParams from '../../hooks/useLocationQueryParams';
import AddRouteForm from './components/AddRouteForm';
import {
  EMPTY_DRIVER,
  EMPTY_ODF_DRIVER,
  DRIVER_TYPES,
} from '../driverDetails/constants';
import SimpleModal from '../../components/SimpleModal';
import {
  PROCEED,
  CANCEL,
  ADD_NEW_ROUTE,
  ARE_YOU_SURE,
  THIS_ROUT_IS_PLANNED,
  FAILED_TO_FETCH,
} from '../../constants/strings';
import {
  DOUBLE_RUN_ROUTE,
  DOUBLE_RUN_ROUTE_MESSAGE_PT_1,
  DOUBLE_RUN_ROUTE_MESSAGE_PT_2,
  DOUBLE_RUN_ROUTE_VOID_MESSAGE,
} from './messages';
import { createRoute } from '../../store/reducers/route';
import { validateRouteCode } from '../../services/routes';
import {
  isRouteStatusInBuild,
  isRouteStatusVoid,
} from '../../models/routeDetail';
import { CREATE_NEW_ROUTE } from '../../constants/analytics';
import { getSectionList } from '../../services/section';
import { getCurrentUserId } from '../../store/selectors/user';
import useFetchWithCondition from '../../hooks/useFetchWithCondition';
import { LOAD_VALIDATE_ROUTE_CODE_PROCESS } from '../../enums/loaderProcess';
import useLoader from '../../hooks/useLoader';

const AddNewRouteDialog = ({
  open,
  onClose,
  handleCloseModal,
  onSelectDriver,
  handleClickNext,
  handleCreateNewRoute,
  doubleRunConfirm,
  doubleRunClose,
  saveChangesConfirm,
  saveChangesClose,
}) => {
  const saveChangesModalRef = useRef();
  const doubleRunRouteModalRef = useRef();
  const routeIsPlannedModalRef = useRef();
  const routeNumberRef = useRef();
  const dispatch = useDispatch();
  const { depot: depotCode, date } = useLocationQueryParams();
  const [message, setMessage] = useState('');
  const [driver, setActiveDriver] = useState(EMPTY_DRIVER);
  const [odfDriver, setActiveOdfDriver] = useState(EMPTY_ODF_DRIVER);
  const [section, setActiveSection] = useState(null);
  const [routeNumber, setRouteNumber] = useState(null);
  const [isMultiRunRoute, setIsMultiRunRoute] = useState(false);
  const [isRouteInBuild, setIsRouteInBuild] = useState(false);
  const [isRouteVoid, setIsRouteVoid] = useState(false);
  const uid = useSelector(getCurrentUserId);
  const [showLoader, hideLoader] = useLoader();

  const { data: vehicles, error: vehiclesError } = useFetchWithCondition(
    'vehicles',
    fetchVehicles,
    open
  );
  const { data: drivers, error: driversError } = useFetchWithCondition(
    'drivers',
    async () =>
      (await fetchDepotDrivers(depotCode, date)).map(mapDriverForFormModel),
    open
  );

  const { data: sections, error: sectionsError } = useFetchWithCondition(
    'sections',
    async () => await getSectionList(depotCode, date, uid),
    open
  );

  const initialValues = useMemo(() => {
    const driverData = getInitialDriverDetailsValues(
      driver,
      odfDriver,
      sections
    );
    return { ...driverData, routeNumber };
  }, [driver, sections, odfDriver, routeNumber]);

  const {
    handleSubmit,
    watch,
    trigger,
    control,
    reset,
    formState: { isDirty, errors, isSubmitting },
  } = useForm({
    defaultValues: initialValues,
    mode: 'onSubmit',
  });

  useEffect(() => {
    reset(initialValues);
  }, [initialValues]);

  const handleClose = useCallback(async () => {
    handleCloseModal();

    if (routeNumber) {
      const [, error] = await wrapPromise(
        saveChangesModalRef.current?.showModal()
      );
      if (error) {
        return;
      }
    }
    onClose();
  }, [routeNumber, onClose, handleCloseModal]);

  const handleSelectDriver = useCallback(
    (driverCode) => {
      setActiveDriver(getDriverByCode(drivers, driverCode));
      onSelectDriver(driverCode);
    },
    [drivers, onSelectDriver]
  );

  const updateSelectedOdfDriver = useCallback(
    (driverCode) => {
      const selectedOdfDriver = getDriverByCode(
        drivers,
        driverCode,
        EMPTY_ODF_DRIVER
      );
      setActiveOdfDriver(selectedOdfDriver);
    },
    [drivers]
  );

  const handleSelectedOdfDriver = useCallback(
    (driverCode) => {
      if (!driverCode || driverCode === EMPTY_ODF_DRIVER.driverCode) {
        setActiveDriver(EMPTY_DRIVER);
      } else if (driver?.driverType !== DRIVER_TYPES.LINKED) {
        setActiveDriver(getDriverData(drivers, { driverCode }, section));
      }
      updateSelectedOdfDriver(driverCode);
    },
    [updateSelectedOdfDriver, driver, drivers, section]
  );

  const handleSelectSection = useCallback((sectionCode) => {
    setActiveSection({ sectionCode });
  }, []);

  const handleNext = useCallback(
    async ({ routeNumber }) => {
      handleClickNext();
      const isRouteNumberValid = await trigger('routeNumber', {
        shouldFocus: true,
      });

      if (!isRouteNumberValid) {
        hideLoader(LOAD_VALIDATE_ROUTE_CODE_PROCESS);
        return;
      }
      showLoader(LOAD_VALIDATE_ROUTE_CODE_PROCESS);

      try {
        const { routeFound, driverCode, routeStatus, odfRouteOwner } =
          await validateRouteCode(depotCode, routeNumber, date);

        if (routeFound) {
          hideLoader(LOAD_VALIDATE_ROUTE_CODE_PROCESS);
          let error;
          if (isRouteStatusInBuild({ status: routeStatus })) {
            [, error] = await wrapPromise(
              routeIsPlannedModalRef.current.showModal()
            );
            setIsRouteInBuild(true);
          } else {
            setIsRouteVoid(isRouteStatusVoid({ status: routeStatus }));
            [, error] = await wrapPromise(
              doubleRunRouteModalRef.current.showModal()
            );
          }

          if (error) {
            routeNumberRef.current.focus();
            return;
          }
          handleSelectDriver(driverCode);
        }

        updateSelectedOdfDriver(
          getRouteOdfDriver(drivers, { odfRouteOwner, driverCode })
        );
        handleSelectDriver(driverCode);
        setIsMultiRunRoute(!!routeFound);
        setRouteNumber(routeNumber);
        hideLoader(LOAD_VALIDATE_ROUTE_CODE_PROCESS);
      } catch (e) {
        const error = pathOr(SOMETHING_WENT_WRONG, ['error', 'message'], e);
        hideLoader(LOAD_VALIDATE_ROUTE_CODE_PROCESS);
        setMessage(error);
      }
    },
    [
      date,
      depotCode,
      handleSelectDriver,
      updateSelectedOdfDriver,
      handleClickNext,
      showLoader,
      hideLoader,
      drivers,
    ]
  );

  const handleConfirm = useCallback(
    async (values) => {
      handleCreateNewRoute();
      const isSuccessful = await dispatch(
        createRoute({ ...values, odfDriver }, isMultiRunRoute)
      );
      isSuccessful && onClose();
    },
    [isMultiRunRoute, onClose, handleCreateNewRoute, odfDriver, dispatch]
  );

  const closeSnackbar = useCallback(() => {
    setMessage('');
  }, []);

  const showMessage = useCallback((msgText) => {
    setMessage(msgText);
  }, []);

  const showValidationError = useCallback(() => {
    setMessage(PLEASE_FIX_ERRORS);
  }, []);

  const isDataReady = useMemo(
    () =>
      vehicles &&
      drivers &&
      sections &&
      !driversError &&
      !vehiclesError &&
      !sectionsError,
    [vehicles, drivers, sections, driversError, vehiclesError, sectionsError]
  );

  const failedToFetch = useMemo(
    () => driversError || vehiclesError || sectionsError,
    [driversError, vehiclesError, sectionsError]
  );

  useEffect(() => {
    if (failedToFetch) {
      setMessage(FAILED_TO_FETCH);
    } else {
      message === FAILED_TO_FETCH && closeSnackbar();
    }
  }, [failedToFetch, message, closeSnackbar]);

  const driversList = useMemo(
    () => getFormattedDriversList(drivers, odfDriver),
    [drivers, odfDriver]
  );

  const odfDriversList = useMemo(
    () => getSortedOdfDriversList(drivers),
    [drivers]
  );

  return (
    <SimpleModal
      onClose={handleClose}
      open={open}
      title={ADD_NEW_ROUTE}
      DialogProps={{
        maxWidth: 'lg',
        transitionDuration: 0,
      }}
      loadId={CREATE_NEW_ROUTE.LOAD}
    >
      {isDataReady ? (
        <ThemeProvider theme={theme}>
          <form
            onSubmit={handleSubmit(handleConfirm)}
            style={{ width: '100%' }}
          >
            <AddRouteForm
              vehicles={vehicles}
              sections={sections}
              drivers={driversList}
              driver={driver}
              isSubmitting={isSubmitting}
              handleNext={handleNext}
              handleSelectDriver={handleSelectDriver}
              handleSelectSection={handleSelectSection}
              dirty={isDirty}
              routeNumber={routeNumber}
              values={watch()}
              isMultiRunRoute={isMultiRunRoute}
              routeNumberRef={routeNumberRef}
              isRouteInBuild={isRouteInBuild}
              odfDriver={odfDriver}
              odfDrivers={odfDriversList}
              handleSelectOdfDriver={handleSelectedOdfDriver}
              control={control}
              showMessage={showMessage}
            />
            <ValidationEffect
              isSubmitting={isSubmitting}
              isValid={
                !Object.values(errors).length ||
                (errors.routeNumber && Object.values(errors).length === 1)
              }
              callback={showValidationError}
            />
          </form>
        </ThemeProvider>
      ) : (
        !failedToFetch && <CircularProgress />
      )}
      <ModalControlled
        Component={ConfirmSnackbar}
        ref={saveChangesModalRef}
        messageIcon={<WarningIcon />}
        trackConfirm={saveChangesConfirm}
        trackClose={saveChangesClose}
      >
        {YOU_HAVE_UNSAVED_CHANGES}
        <br />
        {ARE_YOU_WANT_TO_DISCARD}
      </ModalControlled>
      <Snackbar
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        open={!!message}
        autoHideDuration={!failedToFetch ? 5000 : null}
        message={message}
        onClose={closeSnackbar}
      />
      <ModalControlled
        Component={ConfirmSnackbar}
        ref={doubleRunRouteModalRef}
        confirmText={PROCEED}
        declineText={CANCEL}
        onConfirmDisable={isRouteVoid}
        trackConfirm={doubleRunConfirm}
        trackClose={doubleRunClose}
      >
        {DOUBLE_RUN_ROUTE}
        <br />
        {!isRouteVoid
          ? DOUBLE_RUN_ROUTE_MESSAGE_PT_1
          : DOUBLE_RUN_ROUTE_VOID_MESSAGE}
        <br />
        {!isRouteVoid && DOUBLE_RUN_ROUTE_MESSAGE_PT_2}
      </ModalControlled>
      <ModalControlled
        Component={ConfirmSnackbar}
        ref={routeIsPlannedModalRef}
        confirmText={PROCEED}
        declineText={CANCEL}
      >
        {ARE_YOU_SURE}
        <br />
        {THIS_ROUT_IS_PLANNED}
      </ModalControlled>
    </SimpleModal>
  );
};

export default withTrackProps({
  handleCloseModal: CREATE_NEW_ROUTE.CLICK_CLOSE,
  onSelectDriver: (value) =>
    createLogEntryPayload(CREATE_NEW_ROUTE.ON_SELECT_DRIVER, { value }),
  handleClickNext: CREATE_NEW_ROUTE.ON_CLICK_NEXT,
  handleCreateNewRoute: CREATE_NEW_ROUTE.CLICK_CREATE_ROUTE,
  doubleRunConfirm: CREATE_NEW_ROUTE.CLICK_PROCEED_DOUBLE_ROUTE,
  doubleRunClose: CREATE_NEW_ROUTE.CLICK_CANCEL_DOUBLE_ROUTE,
  saveChangesConfirm: CREATE_NEW_ROUTE.CONFIRM_UNSAVED_CHANGES,
  saveChangesClose: CREATE_NEW_ROUTE.CANCEL_UNSAVED_CHANGES,
})(memo(AddNewRouteDialog));
